// Board format for Explainer videos: one "board" (a hand-drawn-style slide
// whose elements pop in on the narration) per editor clip, stored in
// ve_media_items.metadata.board.
//
// This folder is shared by the in-app Player (src/components/video-editor)
// and the Lambda export bundle (remotion/), so it must stay self-contained:
// relative imports only, no `@/` aliases, and nothing beyond react, remotion
// and @remotion/* (both bundles provide those).

import type { FigureId, SkinTone } from './figures';

export const BOARD_SPEC_VERSION = 1;

export type BoardAspect = '16:9' | '9:16';

/**
 * A board's look (themes.ts): classic pops in, whiteboard and chalkboard are
 * drawn, and the premium looks (looks/) have their own renderers.
 */
export type ThemeId =
  | 'classic'
  | 'whiteboard'
  | 'chalkboard'
  | 'glass'
  | 'kinetic'
  | 'paper'
  | 'neon'
  | 'evidence'
  | 'newsprint'
  | 'blueprint'
  | 'notebook'
  | 'watercolor'
  | 'claymation'
  | 'engraving'
  | 'comic'
  | 'riso'
  | 'pixel';

export const THEME_IDS: readonly ThemeId[] = [
  'classic',
  'whiteboard',
  'chalkboard',
  'glass',
  'kinetic',
  'paper',
  'neon',
  'evidence',
  'newsprint',
  'blueprint',
  'notebook',
  'watercolor',
  'claymation',
  'engraving',
  'comic',
  'riso',
  'pixel',
];

/**
 * Looks illustrated for each video: their objects and characters are drawn
 * by an image model in the look's style (lib/explainer/art.ts) instead of
 * Fluent Emoji icons and figures. A board keeps an emoji for every drawn
 * object, so the other looks still draw it.
 */
export type ArtLook = 'watercolor' | 'claymation' | 'engraving';

export const ART_LOOKS: readonly ArtLook[] = [
  'watercolor',
  'claymation',
  'engraving',
];

export const isArtLook = (look: unknown): look is ArtLook =>
  typeof look === 'string' && (ART_LOOKS as readonly string[]).includes(look);

/** Seconds from the start of the board. Resolved when the project is made. */
export type At = number;

/**
 * Spec fields holding an `At`: `at` and any `…At` (`titleAt`). The planner
 * fills them with narration word indices, timing turns those into seconds.
 */
export const isTimeKey = (key: string) => /^at$|At$/.test(key);

export type CharacterColor =
  | 'green'
  | 'gray'
  | 'blue'
  | 'purple'
  | 'red'
  | 'orange'
  | 'brown'
  | 'beige';

export type Accessory = 'tie' | 'tophat' | 'flower';

/** How a bean character feels (its face); none is calm. */
export type CharacterMood = 'happy' | 'sad' | 'surprised' | 'angry';
export const CHARACTER_MOODS: readonly CharacterMood[] = [
  'happy',
  'sad',
  'surprised',
  'angry',
];

/** What a bean character does with its arms; none keeps them down. */
export type CharacterPose = 'wave' | 'point' | 'cheer' | 'think';
export const CHARACTER_POSES: readonly CharacterPose[] = [
  'wave',
  'point',
  'cheer',
  'think',
];

export type HighlightColor = 'green' | 'red';

export interface Highlight {
  word: string;
  color: HighlightColor;
}

export interface Line {
  at: At;
  text: string;
}

export interface CharacterSpec {
  color: CharacterColor;
  accessory?: Accessory;
  /**
   * Who the character is in looks that draw people (figures.ts), e.g.
   * "woman-teacher" or "cat"; the classic looks draw a bean in `color`.
   */
  figure?: FigureId;
  /** A person figure's skin tone. */
  tone?: SkinTone;
  /** Shown under the character, e.g. "accountant". */
  name?: string;
  /** When the character pops in (default: the board's start). */
  at?: At;
  /**
   * The character drawn for this video (`image:<id>`, a BoardImage with
   * `art`), in its mood and pose: shown in the look it was drawn in, the
   * bean or figure everywhere else.
   */
  art?: string;
  /** Speech lines; each replaces the previous one. */
  lines?: Line[];
  /** An icon the character holds. */
  prop?: { icon: string; side: 'left' | 'right'; at?: At };
  /** Its face (bean looks; people figures keep their own). */
  mood?: CharacterMood;
  /** Its arms (bean looks): waving, pointing, cheering or thinking. */
  pose?: CharacterPose;
}

export interface HeaderSpec {
  text: string;
  highlights?: Highlight[];
  at?: At;
  /** Already on screen from the previous board: no entrance animation. */
  carried?: boolean;
}

export interface IconSpec {
  icon: string;
  label?: string;
  at?: At;
}

export type Slot = 'left' | 'right' | 'top-left' | 'top-right';

export interface ExtraSpec extends IconSpec {
  slot: Slot;
}

export type ArrowColor = 'black' | 'red' | 'green';

export interface ArrowSpec {
  label?: string;
  color?: ArrowColor;
  icon?: string;
  at?: At;
}

export interface StatSpec {
  /** Counts up from 0 when it appears. */
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  icon?: string;
  at?: At;
}

/**
 * A picture of the user's own (a logo, a product photo, a screenshot),
 * stored with the board. Any icon field may name one as `image:<id>`
 * (imageRef): it's drawn in the icon's place, framed by the look.
 */
export interface BoardImage {
  /** Lowercase letters and digits, unique within the board. */
  id: string;
  /** Our own copy (explainer/<user>/<project>/…), never the upload. */
  url: string;
  /** px of the stored file. */
  width: number;
  height: number;
  /**
   * Transparent around the subject (a logo, a cut-out product): drawn on
   * the board as it is, without a photo frame.
   */
  cutout?: boolean;
  /**
   * A cutout's own colour, light or dark: on a surface of the same tone it
   * sits on a plate of the other, or a white logo vanishes on a white board.
   */
  tone?: 'light' | 'dark';
  /**
   * A photograph of a scene or a person (not a screenshot, logo, product
   * or chart): print looks may reproduce it as a halftone newspaper photo
   * without losing anything the narration points at.
   */
  photo?: boolean;
  /**
   * A photo with too little brightness contrast for a halftone (flat, or
   * mostly foliage; measured when it's stored): print looks print it sharp.
   */
  lowContrast?: boolean;
  /**
   * A capture of a web page (a receipt board's source): print looks show it
   * as a clipping cut from the page, sharp, never as a halftone.
   */
  page?: boolean;
  /** What it shows ("The Acme logo"): alt text and the inspector. */
  label?: string;
  /**
   * Drawn for this video in an art look (lib/explainer/art.ts): the look
   * shows it only when it's the look it was drawn in, and any other look
   * draws `icon` (objects) or the character's bean or figure instead.
   */
  art?: BoardArt;
}

export interface BoardArt {
  /** The look it was drawn in. */
  look: ArtLook;
  /** The video's art item it is (the art set's key), for restyling. */
  key: string;
  /** An object's stand-in emoji icon in the looks that don't draw art. */
  icon?: string;
}

const IMAGE_REF_PREFIX = 'image:';

/** The icon value that shows a board image. */
export const imageRef = (id: string) => `${IMAGE_REF_PREFIX}${id}`;

/** The image id an icon value names, if it names one. */
export const imageRefId = (name: string | undefined): string | undefined =>
  name?.startsWith(IMAGE_REF_PREFIX)
    ? name.slice(IMAGE_REF_PREFIX.length) || undefined
    : undefined;

/** Images a board carries at most (the form takes 6 uploads). */
export const BOARD_IMAGES_MAX = 6;

/**
 * Pictures one board may carry: the user's pictures, a receipt or photo,
 * and in the art looks a drawing for every object and character (a dialog
 * holds 4 characters, 4 props and 2 extras).
 */
export const BOARD_IMAGES_PER_BOARD = 16;

export interface ListItemSpec {
  icon: string;
  text: string;
  highlights?: Highlight[];
  at?: At;
}

interface BoardBase {
  version: typeof BOARD_SPEC_VERSION;
  /** The canvas the board was laid out for. */
  aspect: BoardAspect;
  /** The look; missing means classic (boards made before looks existed). */
  theme?: ThemeId;
  /**
   * The narration's language (ISO 639-1), which picks the handwriting font
   * for its alphabet, so every board of a video uses the same one.
   */
  lang?: string;
  header?: HeaderSpec;
  /**
   * The narration's words spoken during the board, as written in the script
   * (punctuation included), each with its time: [seconds from the board's
   * start, word]. Kinetic Type sets them on screen.
   */
  words?: BoardWord[];
  /** false turns the look's sound effects off (looks/ renderers play them). */
  sfx?: boolean;
  /**
   * 'film': the board as if shot on film, over any look: grain, a vignette,
   * a slight flicker and gate weave.
   */
  finish?: 'film';
  /** The user's pictures this board shows (icon fields name them). */
  images?: BoardImage[];
}

/** [seconds from the board's start, the word as written in the script]. */
export type BoardWord = [At, string];

export type DialogBoard = BoardBase & {
  template: 'dialog';
  characters: CharacterSpec[];
  extras?: ExtraSpec[];
};
export type TitleBoard = BoardBase & {
  template: 'title';
  character: CharacterSpec;
  title: string;
  titleAt?: At;
  /**
   * A picture of the user's (`image:<id>`, usually their logo or product)
   * shown in the character's place.
   */
  image?: string;
};
export type ChapterBoard = BoardBase & {
  template: 'chapter';
  title: string;
  icon?: string;
  /**
   * Which chapter card of the video this is (1-based), stamped when boards
   * are timed: Pixel Quest shows it as "LEVEL 2".
   */
  level?: number;
};
export type SplitBoard = BoardBase & {
  template: 'split';
  source: IconSpec;
  branches: (IconSpec & { color?: HighlightColor })[];
};
export type CycleBoard = BoardBase & {
  template: 'cycle';
  arrowStyle: 'straight' | 'curved';
  actor: CharacterSpec;
  out: ArrowSpec;
  target: { icons: string[]; label?: string; at?: At };
  back: ArrowSpec;
};
export type StatsBoard = BoardBase & { template: 'stats'; stats: StatSpec[] };
export type ListBoard = BoardBase & {
  template: 'list';
  items: ListItemSpec[];
  character?: CharacterSpec;
};

/**
 * `bars`: columns (rows in portrait) that grow to their values. `line`: a
 * trend drawn point by point. `donut`: parts of a whole (2-3). `pictogram`:
 * "7 in 10" as icons.
 */
export type ChartKind = 'bars' | 'line' | 'donut' | 'pictogram';

export const CHART_KINDS: readonly ChartKind[] = [
  'bars',
  'line',
  'donut',
  'pictogram',
];

export interface ChartDatum {
  /** What the value is ("2020", "Apple", "Women"). */
  label: string;
  /** The number, read from the narration (scale applied). */
  value: number;
  /** The value as shown ("$355K", "40%", "1,2 Mio."). */
  display: string;
  icon?: string;
  /** The value the board is about: drawn in the accent colour. */
  highlight?: boolean;
  at?: At;
}

export type ChartBoard = BoardBase & {
  template: 'chart';
  kind: ChartKind;
  data: ChartDatum[];
  /** The values are percentages: a donut is a share of 100. */
  percent?: boolean;
  /** Pictogram: how many icons make the whole (2-20, default 10). */
  total?: number;
  /** A short source line under the chart ("Source: World Bank"). */
  source?: string;
  /** When the axis or the empty ring appears (default: with the board). */
  at?: At;
};

export interface TimelineEvent {
  /** As shown: "1969", "1990s". */
  year: string;
  label: string;
  icon?: string;
  at?: At;
}

export type TimelineBoard = BoardBase & {
  template: 'timeline';
  events: TimelineEvent[];
  /** When the axis appears (default: with the board). */
  at?: At;
};

/** A map's colours, from the look's palette. */
export type MapColor = 'green' | 'orange' | 'purple' | 'red' | 'blue';

/** In the order places get them when the planner gives none. */
export const MAP_COLORS: readonly MapColor[] = [
  'green',
  'orange',
  'purple',
  'red',
  'blue',
];

/** A point in map units (explainer/maps.ts: Miller, 100 per degree). */
export type MapPoint = [number, number];
/** [left, top, right, bottom] in map units. */
export type MapBox = [number, number, number, number];

export interface MapPlaceSpec {
  /**
   * A country is coloured in, a region (a continent, the EU, Southeast
   * Asia) colours in all its countries, a city is a dot.
   */
  kind: 'country' | 'region' | 'city';
  /** A country's id in the map data (Natural Earth's ADM0_A3: "FRA"). */
  id?: string;
  /** A region's countries. */
  ids?: string[];
  label: string;
  /** Where the label (country, region) or the dot (city) goes. */
  point: MapPoint;
  /** A country's main territory, a region's middle: what the camera frames. */
  box?: MapBox;
  color?: MapColor;
  at?: At;
}

export interface MapRouteSpec {
  /** Indices into the board's places. */
  from: number;
  to: number;
  color?: MapColor;
  at?: At;
}

/**
 * Another view of places an earlier map showed: "wide" pulls out to their
 * surroundings, "close" moves in closer.
 */
export type MapViewStyle = 'wide' | 'close';

export type MapBoard = BoardBase & {
  template: 'map';
  places: MapPlaceSpec[];
  /** Arrows from one place to another (trade, travel, migration). */
  routes?: MapRouteSpec[];
  /** Set on a map repeating an earlier map's places (planner.ts). */
  view?: MapViewStyle;
};

export interface QuizOptionSpec {
  text: string;
  icon?: string;
  at?: At;
}

/**
 * A question (the header) with 2-4 options; a 3-2-1 countdown runs in the
 * seconds before `revealAt`, when the right option turns green.
 */
export type QuizBoard = BoardBase & {
  template: 'quiz';
  header: HeaderSpec;
  options: QuizOptionSpec[];
  /** Index of the right option. */
  answer: number;
  revealAt: At;
};

export type VersusSideName = 'left' | 'right';

export interface VersusSideSpec {
  label: string;
  icon: string;
  at?: At;
}

/** One point compared: what it is, and each side's value. */
export interface VersusRowSpec {
  label: string;
  left: string;
  right: string;
  /** The side that wins this point: its value is drawn in green. */
  winner?: VersusSideName;
  at?: At;
}

/** Two things side by side ("Coffee vs Tea"), compared point by point. */
export type VersusBoard = BoardBase & {
  template: 'versus';
  /** Left (top in portrait) and right. */
  sides: [VersusSideSpec, VersusSideSpec];
  rows?: VersusRowSpec[];
  /** The overall winner, crowned at `winnerAt`. */
  winner?: VersusSideName;
  winnerAt?: At;
};

export interface RankItemSpec {
  /** 1 is the top (a board may hold 6-10 of a longer ranking). */
  rank: number;
  text: string;
  icon?: string;
  at?: At;
}

/**
 * A ranking ("Top 5…"): numbered slots shown empty from the start, each
 * filled as its item is named (usually counting down to number 1).
 */
export type RankingBoard = BoardBase & {
  template: 'ranking';
  items: RankItemSpec[];
};

/**
 * One of the user's pictures, large (a screenshot, a product, a photo),
 * under the header with a caption below it.
 */
export type PictureBoard = BoardBase & {
  template: 'picture';
  /** `image:<id>` */
  image: string;
  /** When the picture appears (default: with the board). */
  at?: At;
  /** A short line under the picture. */
  caption?: string;
  captionAt?: At;
};

/** A rectangle on a picture, in the picture's own px: [left, top, width, height]. */
export type PictureRect = [number, number, number, number];

/**
 * A "receipt": one of the video's sources on screen, a capture of the page
 * (its headline and the quoted passage), the quote swept with a highlighter
 * on the word the narrator says it, the site named under it.
 */
export type ReceiptBoard = BoardBase & {
  template: 'receipt';
  /** `image:<id>`: the capture (a BoardImage with `page: true`). */
  image: string;
  /** When the capture appears (default: with the board). */
  at?: At;
  /** The quoted sentence as the page writes it (alt text, Kinetic Type). */
  quote: string;
  /** The quote's lines on the capture, one rectangle each. */
  marks: PictureRect[];
  /** When the highlighter sweeps the quote. */
  markAt?: At;
  /** The site's name ("Ember", "USDA Foreign Agricultural Service"). */
  site: string;
  /** The page's address as shown ("ember-energy.org/latest-insights/…"). */
  url?: string;
};

/**
 * A photograph (a licensed stock photo), large, pushed in slowly while it's
 * on screen, with a short caption and the photographer's credit.
 */
export type PhotoBoard = BoardBase & {
  template: 'photo';
  /** `image:<id>`: the photo (a BoardImage with `photo: true`). */
  image: string;
  at?: At;
  caption?: string;
  captionAt?: At;
  /** "Photo: Jane Doe / Pexels". */
  credit?: string;
};

export type BoardSpec =
  | DialogBoard
  | TitleBoard
  | ChapterBoard
  | SplitBoard
  | CycleBoard
  | StatsBoard
  | ListBoard
  | ChartBoard
  | TimelineBoard
  | MapBoard
  | QuizBoard
  | VersusBoard
  | RankingBoard
  | PictureBoard
  | ReceiptBoard
  | PhotoBoard;

export type BoardTemplate = BoardSpec['template'];

export const BOARD_TEMPLATES: readonly BoardTemplate[] = [
  'dialog',
  'title',
  'chapter',
  'split',
  'cycle',
  'stats',
  'list',
  'chart',
  'timeline',
  'map',
  'quiz',
  'versus',
  'ranking',
  'picture',
  'receipt',
  'photo',
];

/** How many places and routes a map shows. */
export const MAP_LIMITS = { places: 6, routes: 3 } as const;

/** Quiz options, versus rows and ranking items a board shows: [min, max]. */
export const QUIZ_OPTIONS: [number, number] = [2, 4];
export const VERSUS_ROWS = 3;
export const rankingRange = (aspect: BoardAspect): [number, number] =>
  aspect === '9:16' ? [2, 5] : [2, 5];

/** Seconds the quiz countdown takes at most (one per number). */
export const QUIZ_COUNTDOWN = 3;

/** How many values a chart of a kind shows on a canvas: [min, max]. */
export function chartDataRange(
  kind: ChartKind,
  aspect: BoardAspect,
): [number, number] {
  const portrait = aspect === '9:16';
  switch (kind) {
    case 'bars':
      return [2, portrait ? 5 : 6];
    case 'line':
      return [3, portrait ? 6 : 8];
    case 'donut':
      return [2, 3];
    case 'pictogram':
      return [1, 1];
  }
}

/** How many events a timeline shows on a canvas: [min, max]. */
export const timelineRange = (aspect: BoardAspect): [number, number] =>
  aspect === '9:16' ? [2, 4] : [2, 5];

export const CHARACTER_COLORS: readonly CharacterColor[] = [
  'green',
  'gray',
  'blue',
  'purple',
  'red',
  'orange',
  'brown',
  'beige',
];

export const ACCESSORIES: readonly Accessory[] = ['tie', 'tophat', 'flower'];

/** Per-template caps; the renderer drops anything beyond them. */
export const BOARD_LIMITS = {
  dialogCharacters: 4,
  extras: 2,
  lines: 6,
  branches: 2,
  cycleIcons: 4,
  stats: 3,
  listItems: 4,
  highlights: 4,
  words: 600,
  /** A receipt's quote: lines highlighted on the capture. */
  receiptMarks: 12,
} as const;
